import React, { useState } from 'react';
import { Send, User, Mail, Phone, MessageSquare, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { projectInfo } from '../data/mock';

const ContactSection = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/leads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        throw new Error('Failed to submit form');
      }

      setIsSubmitted(true);
      setFormData({ name: '', email: '', phone: '', message: '' });

      // Reset success message after 5 seconds
      setTimeout(() => setIsSubmitted(false), 5000);
    } catch (err) {
      setError('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-24 bg-gradient-to-b from-[#0a1628] to-[#0d1f35]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid lg:grid-cols-2 gap-16">
          {/* Left Side - Info */}
          <div>
            <span className="text-[#c9a961] text-sm font-semibold tracking-widest uppercase">Get In Touch</span>
            <h2 className="text-4xl sm:text-5xl font-bold text-white mt-3 mb-6">Enquire Now</h2>
            <p className="text-white/70 text-lg mb-10">
              Take the first step towards your dream home. Our experts are ready to assist you with all your queries.
            </p>

            {/* Contact Cards */}
            <div className="space-y-4">
              <a
                href={`tel:${projectInfo.phone}`}
                className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-[#c9a961]/30 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-[#c9a961]/20 to-[#c9a961]/5 rounded-xl flex items-center justify-center">
                  <Phone className="w-6 h-6 text-[#c9a961]" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">Call Us</p>
                  <p className="text-white font-semibold text-lg">+91 9066840485</p>
                </div>
              </a>

              <a
                href={`https://api.whatsapp.com/send?phone=919066840485&text=${encodeURIComponent(projectInfo.whatsappMessage)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-4 bg-white/5 backdrop-blur-sm border border-white/10 rounded-xl p-5 hover:bg-white/10 hover:border-green-500/30 transition-all duration-300"
              >
                <div className="w-14 h-14 bg-gradient-to-br from-green-500/20 to-green-500/5 rounded-xl flex items-center justify-center">
                  <MessageSquare className="w-6 h-6 text-green-500" />
                </div>
                <div>
                  <p className="text-white/60 text-sm">WhatsApp</p>
                  <p className="text-white font-semibold text-lg">Chat with us</p>
                </div>
              </a>
            </div>
          </div>

          {/* Right Side - Form */}
          <div className="relative">
            <div className="bg-white/5 backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-10">
              {isSubmitted ? (
                <div className="text-center py-12">
                  <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle className="w-10 h-10 text-green-500" />
                  </div>
                  <h3 className="text-2xl font-bold text-white mb-3">Thank You!</h3>
                  <p className="text-white/70">We've received your inquiry. Our team will contact you shortly.</p>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Full Name *</label>
                    <div className="relative">
                      <User className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        placeholder="Enter your name"
                        className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-14 rounded-xl focus:border-[#c9a961] focus:ring-[#c9a961]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Email Address *</label>
                    <div className="relative">
                      <Mail className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        placeholder="Enter your email"
                        className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-14 rounded-xl focus:border-[#c9a961] focus:ring-[#c9a961]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Phone Number *</label>
                    <div className="relative">
                      <Phone className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-white/40" />
                      <Input
                        type="tel"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        placeholder="+91 XXXXX XXXXX"
                        className="pl-12 bg-white/5 border-white/10 text-white placeholder:text-white/40 h-14 rounded-xl focus:border-[#c9a961] focus:ring-[#c9a961]/20"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="text-white/60 text-sm mb-2 block">Message (Optional)</label>
                    <Textarea
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      placeholder="Tell us about your requirements..."
                      rows={4}
                      className="bg-white/5 border-white/10 text-white placeholder:text-white/40 rounded-xl focus:border-[#c9a961] focus:ring-[#c9a961]/20 resize-none"
                    />
                  </div>

                  {error && (
                    <p className="text-red-400 text-sm">{error}</p>
                  )}

                  <Button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-[#c9a961] to-[#a08545] hover:from-[#d4b76d] hover:to-[#b8964d] text-[#0a1628] font-bold py-6 rounded-xl text-lg transition-all duration-300 hover:shadow-lg hover:shadow-[#c9a961]/30 disabled:opacity-50"
                  >
                    {isSubmitting ? (
                      <span className="flex items-center gap-2">
                        <div className="w-5 h-5 border-2 border-[#0a1628]/30 border-t-[#0a1628] rounded-full animate-spin" />
                        Submitting...
                      </span>
                    ) : (
                      <span className="flex items-center gap-2">
                        <Send className="w-5 h-5" />
                        Submit Enquiry
                      </span>
                    )}
                  </Button>
                </form>
              )}
            </div>

            {/* Decorative Elements */}
            <div className="absolute -top-4 -right-4 w-24 h-24 border-2 border-[#c9a961]/20 rounded-2xl -z-10" />
            <div className="absolute -bottom-4 -left-4 w-32 h-32 border-2 border-[#c9a961]/10 rounded-full -z-10" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default ContactSection;
